import { useCallback, useEffect, useState } from "react";
import type { HomeworkSummary } from "@edu/shared";
import * as api from "../../../lib/api.ts";
import { dayCountText } from "../../../lib/plurals.ts";
import { leftLabel } from "../../../lib/when.ts";
import { ChevronIcon } from "../../../lib/RowDisclosure.tsx";
import { Empty } from "../../../lib/Empty.tsx";
import { Loading } from "../../../lib/loading.tsx";
import { TRASH_KEEP_DAYS } from "../trash-keep.ts";

/**
 * Корзина хаба: сворачиваемый блок, который грузится по раскрытию (4.3 фазы 2).
 *
 * ⚠️ Отдельный запрос, а не половина ответа списка. С курсором страница живых
 * работ могла бы целиком состоять из корзины, и человек увидел бы пустой список
 * рядом с полной корзиной. Цена решения это один лишний запрос, и он делается
 * только когда блок раскрыли.
 *
 * ⚠️ Курсор здесь тот же, что у живого списка, а не потолок в двести строк:
 * отвязка пары кладёт в корзину ВСЕ работы пары разом, у пары с годовой
 * историей это сотни строк, то есть и обрыв на отсечке 16 КБ, и экран,
 * показывающий часть без признака части.
 *
 * ⚠️ Блок раскрывается сам, когда сюда пришли после «В корзину» с карточки
 * работы: иначе человек, только что удаливший работу, не видит её нигде.
 */

/*
  Показываем «удалится через N дней», а не дату удаления: у корзины спрашивают,
  сколько осталось передумать, а не когда домашку убрали. Сам срок живёт в
  `../trash-keep.ts`, общий с другими подписями про корзину.
*/

export function TrashPanel(props: {
  studentUserId: string | null;
  /** Пришли после «В корзину»: блок раскрыт, эта работа подсвечена. */
  highlightId: string | null;
  /** Восстановление вернуло работу в живой список, его надо перечитать. */
  onRestored: () => void;
}) {
  const { studentUserId, highlightId } = props;
  // Один момент на всю отрисовку: строки обязаны считать «осталось» от одного и
  // того же «сейчас», иначе соседние строки одной даты разойдутся на день
  const now = Date.now();
  const [open, setOpen] = useState(highlightId !== null);
  const [rows, setRows] = useState<HomeworkSummary[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (cursor: string | null): Promise<void> => {
      setBusy(true);
      try {
        const res = await api.listHomeworkTrash({ studentUserId, cursor });
        // `trash` необязателен с шага А4: в обычном ответе списка его больше нет
        // вовсе, и заполняется он только здесь, в ответе на `?trash=1`
        const got = res.trash ?? [];
        setRows((prev) => (cursor === null || prev === null ? got : [...prev, ...got]));
        setNextCursor(res.nextCursor ?? null);
        setErr(null);
      } catch {
        setErr("Не вышло загрузить корзину. Попробуйте ещё раз.");
      } finally {
        setBusy(false);
      }
    },
    [studentUserId],
  );

  // Раскрыли блок или сменили разрез: читаем заново. Свёрнутый блок не грузится
  useEffect(() => {
    if (!open) return;
    setRows(null);
    setNextCursor(null);
    void load(null);
  }, [open, load]);

  // Пришли после «В корзину»: блок обязан раскрыться, даже если был свёрнут
  useEffect(() => {
    if (highlightId !== null) setOpen(true);
  }, [highlightId]);

  const restore = async (homeworkId: string): Promise<void> => {
    setBusy(true);
    try {
      await api.restoreHomework(homeworkId);
      setRows((prev) => (prev === null ? null : prev.filter((h) => h.id !== homeworkId)));
      setErr(null);
      props.onRestored();
    } catch {
      setErr("Не вышло восстановить домашку. Попробуйте ещё раз.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>
        {/*
          ⚠️ Фигура раскрытия ОДНА на всё приложение, и до 14.09.2026 здесь
          стояли два типографских знака (`▾` и `▸`). Знаков-картинок в системе
          нет нигде: шеврон это тот же `ChevronIcon`, что раскрывает строку
          списка, а состояние несёт `aria-expanded`, по которому CSS его и
          поворачивает.
        */}
        <button
          type="button"
          className="btn btn--quiet disclose"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <ChevronIcon />
          Корзина
        </button>
      </h2>

      {open && (
        <>
          <p className="muted">
            Хранится {dayCountText(TRASH_KEEP_DAYS)}, затем домашка удаляется насовсем вместе с
            ответами ученика.
          </p>
          {err !== null && <p className="err">{err}</p>}
          {rows === null && err === null && <Loading />}
          {/* Срок хранения уже сказан строкой выше, и повторять его в плашке
              значит сказать одну мысль дважды */}
          {rows !== null && rows.length === 0 && <Empty icon="trash" title="Корзина пуста" />}

          {rows !== null && rows.length > 0 && (
            <ul className="list list--hw-trash">
              <li className="list__head" aria-hidden="true">
                <span>Ученик</span>
                <span>Название</span>
                <span>Удалится</span>
                <span>Действие</span>
              </li>
              {rows.map((h) => (
                <TrashRow
                  key={h.id}
                  homework={h}
                  fresh={h.id === highlightId}
                  now={now}
                  busy={busy}
                  onRestore={() => void restore(h.id)}
                />
              ))}
            </ul>
          )}

          {nextCursor !== null && (
            <p className="acts">
              <button className="btn" disabled={busy} onClick={() => void load(nextCursor)}>
                Показать ещё
              </button>
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Строка корзины: Ученик | Работа | Удалится | Действие.
 *
 * Главная величина здесь не «когда удалили», а «сколько осталось передумать»:
 * дата удаления сама по себе требует от человека помнить срок хранения и
 * считать в уме. Точная дата уборки при этом никуда не делась, она в подсказке
 * под курсором.
 *
 * ⚠️ Строка приглушена (`data-dim`), но читаема: корзина это то, из чего
 * восстанавливают, и нечитаемый список восстанавливать не из чего.
 */
function TrashRow(props: {
  homework: HomeworkSummary;
  /** Работа, только что убранная в корзину с её карточки: подсвечена. */
  fresh: boolean;
  now: number;
  busy: boolean;
  onRestore: () => void;
}) {
  const h = props.homework;
  const left = h.deletedAt === null ? null : leftLabel(h.deletedAt, TRASH_KEEP_DAYS, props.now);

  return (
    <li className={props.fresh ? "row hub__fresh" : "row"} data-dim={true}>
      <span className="cell" data-label="Ученик">
        {h.counterpartName ?? "…"}
      </span>

      <span className="cell row__name">
        {h.title}
        {props.fresh && <span className="row__sub">только что убрана в корзину</span>}
      </span>

      {/* Даты удаления у строки корзины не быть не может, но тип допускает null,
          и пустой слот в этом месте развалил бы колонки соседних строк */}
      {left === null ? (
        <span className="cell row__void" data-label="Удалится">
          срок неизвестен
        </span>
      ) : (
        <span
          className="cell row__when"
          data-label="Удалится"
          data-tone={left.tone}
          title={left.title}
        >
          {left.text}
        </span>
      )}

      <span className="cell row__act">
        <button className="btn btn--sm" disabled={props.busy} onClick={props.onRestore}>
          Восстановить
        </button>
      </span>
    </li>
  );
}
